<template>
  <div class="col-12 delivery-platforms-tab">
    <placeholder-content
      v-if="loading"
      :is-load="true"
    />
    <access-denied
      v-else-if="!hasPermission"
    />

    <template v-else>
      <div class="card border-0 shadow-sm">
        <div class="card-header bg-white py-2">
          <b style="font-size: 15px;">
            <i class="fa-solid fa-motorcycle me-2 text-primary" />
            {{ $t('_delivery.Delivery platforms of this branch') }}
          </b>
          <div class="text-muted" style="font-size: 12px;">
            {{ $t('_delivery.GP left empty = use the default rate of the channel') }}
          </div>
        </div>
        <div class="card-body p-0">
          <div v-if="!items.length" class="text-center text-muted py-4">
            {{ $t('_delivery.No external delivery channels configured yet') }}
          </div>
          <table v-else class="table table-sm align-middle mb-0">
            <thead>
              <tr class="text-muted small">
                <th class="ps-3">{{ $t('_delivery.Channel') }}</th>
                <th style="width: 110px;">{{ $t('_delivery.Enabled') }}</th>
                <th style="width: 130px;">{{ $t('_delivery.GP %') }}</th>
                <th style="width: 130px;">{{ $t('_delivery.Pickup GP %') }}</th>
                <th style="width: 180px;" class="pe-3">{{ $t('_delivery.Store ID on platform') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td class="ps-3">
                  <span class="badge" :style="{ backgroundColor: item.color || '#6B7280' }">
                    <i v-if="item.icon" :class="item.icon" class="me-1"/>{{ item.name }}
                  </span>
                </td>
                <td>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="item.is_active">
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    class="form-control form-control-sm text-end shadow-none"
                    v-model="item.commission_rate"
                    :placeholder="Number(item.default_commission_rate).toFixed(2)"
                  >
                </td>
                <td>
                  <input
                    v-if="item.has_pickup"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    class="form-control form-control-sm text-end shadow-none"
                    v-model="item.pickup_commission_rate"
                    :placeholder="item.pickup_commission_rate_default !== null && item.pickup_commission_rate_default !== undefined ? Number(item.pickup_commission_rate_default).toFixed(2) : Number(item.default_commission_rate).toFixed(2)"
                  >
                  <span v-else class="text-muted small">-</span>
                </td>
                <td class="pe-3">
                  <input
                    type="text"
                    class="form-control form-control-sm shadow-none"
                    v-model.trim="item.external_store_id"
                    :placeholder="$t('optional')"
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="card-footer bg-white py-2 text-end" v-if="items.length">
          <ladda
            text="Save"
            :btn-class="'btn btn-primary btn-sm'"
            :loading="loadingSave"
            icon="<i class='fa-solid fa-floppy-disk'></i>"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { Branches } from '@/services/endpoint/branches.service';

/**
 * แท็บตั้งค่า delivery platform รายสาขา (docs ฝั่ง api: delivery-platform-pricing.md)
 * - ไม่มีแถวใน BE = เปิด + ใช้ GP default → หน้านี้ upsert branch_delivery_platforms
 * - โชว์เฉพาะ platform ภายนอก (ส่งเอง/in_house ไม่มีอะไรให้ตั้ง)
 */
export default {
  name: 'DeliveryPlatformsTab',
  props: {
    branchId: { type: Number, required: true }
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      items: []
    };
  },
  components: { PlaceholderContent, AccessDenied, Ladda },
  methods: {
    async fetchData() {
      this.loading = true;
      await Branches.getDeliveryPlatforms(this.branchId).then(res => {
        const platforms = res.data.data?.platforms ?? [];
        this.items = platforms
          .filter(p => p.is_external)
          .map(p => ({
            id: p.id,
            name: p.name,
            color: p.color || '',
            icon: p.icon || '',
            default_commission_rate: p.default_commission_rate,
            has_pickup: !!p.has_pickup,
            pickup_commission_rate_default: p.pickup_commission_rate_default,
            is_active: !!p.is_active,
            commission_rate: (p.commission_rate !== null && p.commission_rate !== undefined) ? p.commission_rate : '',
            pickup_commission_rate: (p.pickup_commission_rate !== null && p.pickup_commission_rate !== undefined) ? p.pickup_commission_rate : '',
            external_store_id: p.external_store_id || ''
          }));
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) this.hasPermission = false;
        else if (err?.response?.data?.message) this.$toast.warn(this.$t(err.response.data.message));
      });
      this.loading = false;
    },
    async handleSave() {
      if (this.loadingSave) return;
      this.loadingSave = true;
      const payload = {
        items: this.items.map(item => ({
          delivery_platform_id: item.id,
          is_active: !!item.is_active,
          commission_rate: item.commission_rate === '' ? null : Number(item.commission_rate),
          pickup_commission_rate: item.pickup_commission_rate === '' ? null : Number(item.pickup_commission_rate),
          external_store_id: item.external_store_id || null
        }))
      };
      await Branches.updateDeliveryPlatforms(this.branchId, payload).then(() => {
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Success!'));
        this.fetchData();
      }).catch(err => {
        if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      }).finally(() => {
        this.loadingSave = false;
      });
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>
